import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";

import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import CopyId from "@/components/ui/CopyId";

import { BadgeCheck, CalendarDays, Copy, Eye, IdCard, Mail, Shield, UserRound, X } from "lucide-react";

import InfoCard from "@/components/shared/InfoCard";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { formatDate } from "@/utils/dateUtils";
import { UserService } from "@/services/userService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

const ViewUserModal = ({ open, onClose, user }) => {
  // Local copy of user data for modal stability
  const [current, setCurrent] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [issuing, setIssuing] = useState(false);

  // Sync incoming user when modal opens.
  //
  // Nothing is fetched here. `GET /users/{id}/dev-invite-link` is a GET that
  // WRITES: it retires every unused token the user holds, inserts a new one
  // and mails a fresh invitation. Calling it on open meant that looking at an
  // invited-but-not-accepted user invalidated the link already in that
  // person's inbox — two colleagues checking the same row, or an admin who
  // had copied the link out to send by hand, and the candidate's link answered
  // INVITE_ALREADY_USED. Issuing a link is now something the reader asks for.
  useEffect(() => {
    if (!open) return;
    setCurrent(user);
    setInviteLink(null);
    setIssuing(false);
  }, [open, user]);

  // Only a user who was invited and has not accepted has a link to issue. An
  // AD account never goes through the invite flow, and a SuperAdmin has no
  // tenant invitation.
  const canIssueInviteLink =
    !!current && !current.isActive && current.authProvider !== "ad" && !current.isSuperAdmin;

  const handleIssueInviteLink = async () => {
    if (!current?.id) return;
    setIssuing(true);
    try {
      const link = await UserService.fetchDevInviteLink(current.id);
      setInviteLink(link || null);
      if (!link) toast.error("This environment does not expose invite links.");
    } catch (err) {
      toast.error(apiErrorMessageFormat(err, "Failed to issue invite link."));
    } finally {
      setIssuing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy — copy manually");
    }
  };

  // Only render when modal is open and data exists
  const isOpen = open && !!current;

  // Modal subtitle (user name fallback)
  const title = useMemo(() => current?.name || "User", [current]);

  // Structured user detail fields
  const details = [
    {
      label: "Name",
      value: current?.name || "",
      icon: UserRound,
    },
    {
      label: "Username",
      value: current?.username || "",
      icon: IdCard,
    },
    {
      label: "Email",
      value: current?.email || "",
      icon: Mail,
    },
    {
      label: "Role",
      value: current?.role?.name || current?.role || "—",
      icon: Shield,
    },
    {
      label: "Manager",
      value: current?.manager?.name || "—",
      icon: Eye,
    },
    {
      label: "Status",
      value: current?.isActive ? "Active" : "Inactive",
      icon: BadgeCheck,
    },
    {
      label: "End Date",
      value: current?.endDate ? formatDate(current.endDate) : "—",
      icon: CalendarDays,
    },
    {
      label: "Created At",
      value: current?.createdAt ? formatDate(current.createdAt) : "",
      icon: CalendarDays,
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(v) => {
        // Close modal when dialog requests close
        if (!v) onClose?.();
      }}
    >
      <DialogContent className="w-[560px] md:w-full flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">View User</DialogTitle>

            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5 flex items-center gap-2">
              <span className="truncate">{title}</span>
              <CopyId label="User" value={current?.id} />
            </DialogDescription>
          </div>

          {/* Close button */}
          <DialogClose asChild>
            <span className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
              <X size={16} strokeWidth={3} />
            </span>
          </DialogClose>
        </DialogHeader>

        {/* Content */}
        <div className="space-y-8 p-6 overflow-y-auto custom-scrollbar">
          {/* Details section */}
          <div>
            <h3 className="mb-5 text-lg font-semibold">Details</h3>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {details.map((item, idx) => (
                <InfoCard key={idx} icon={item.icon} label={item.label} value={item.value} currentIndex={idx} totalData={details.length} />
              ))}
            </div>
          </div>

          {/* Dev/UAT-only invite link, issued on request. */}
          {canIssueInviteLink && (
            <div className="border-t border-border-primary pt-5">
              <h3 className="mb-3 text-sm font-semibold text-text-primary">
                Invitation Link <span className="text-[10px] font-normal text-text-secondary">(Dev / UAT only)</span>
              </h3>
              <p className="mb-2 text-[11px] text-text-secondary">
                Issuing a link emails this user a fresh invitation and stops the previous link working. Use it to test the
                accept-invite flow without a real inbox.
              </p>

              {/* The route this button calls needs `user.create` — the same
                  code the Add User and Resend Invite controls carry. The modal
                  itself opens on `user.view`, so without this guard a
                  view-only reader got a silent 403. */}
              <PermissionGuard permissions={[PERMISSIONS.USER_CREATE, PERMISSIONS.SUPER_ADMIN_MANAGE]} isComponent>
                <Button
                  type="button"
                  onClick={handleIssueInviteLink}
                  disabled={issuing}
                  className="mb-3 px-3 py-2 rounded-md bg-button-secondary text-text-base text-[12px] flex items-center gap-1.5"
                >
                  <Mail size={14} />
                  {issuing ? "Issuing…" : inviteLink ? "Issue a new invite link" : "Issue an invite link"}
                </Button>
              </PermissionGuard>
            </div>
          )}

          {inviteLink && (
            <div className="border-t border-border-primary pt-5">
              <h3 className="mb-3 text-sm font-semibold text-text-primary">The link just issued</h3>
              <div className="flex items-stretch gap-2">
                <Input
                  type="text"
                  readOnly
                  value={inviteLink}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 rounded-md border-border-primary bg-background-primary/40 px-3 py-2 text-[12px] text-text-primary"
                />
                <Button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-3 py-2 rounded-md bg-button-secondary text-text-base text-[12px] flex items-center gap-1.5 shrink-0"
                >
                  <Copy size={14} />
                  Copy
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewUserModal;
